import { useState } from "react";
import { motion } from "framer-motion";
import { Bell, Package, Users, ShoppingCart, CreditCard, FileText, Building, AlertTriangle } from "lucide-react";
import { Card, CardBody, Badge, ScrollShadow } from "@heroui/react";
import OrderGraph from "../components/orderGraph";

// 🎨 Animation Framer Motion pour chaque section Bento
const bentoVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

// 🎯 Fake Data pour le Dashboard
const fakeData = {
  notifications: [
    { id: 1, message: "Nouvelle commande reçue", read: false },
    { id: 2, message: "Stock faible sur un produit", read: false },
    { id: 3, message: "Stock faible sur un produit", read: false },
    { id: 4, message: "Stock faible sur un produit", read: false },
    { id: 5, message: "Stock faible sur un produit", read: false },
    { id: 6, message: "Stock faible sur un produit", read: false },
    { id: 7, message: "Stock faible sur un produit", read: false },
    { id: 8, message: "Stock faible sur un produit", read: false },
    { id: 9, message: "Stock faible sur un produit", read: false },
    { id: 10, message: "Stock faible sur un produit", read: false },
  ],
  orders: [
    { id: "ORD123", status: "pending" },
    { id: "ORD124", status: "shipped" },
    { id: "ORD125", status: "delivered" },
    { id: "ORD126", status: "delivered" },
    { id: "ORD127", status: "delivered" },
    { id: "ORD128", status: "delivered" },
    
  ],
  clientsInd: [
    { id: "CLI1", name: "Jean Dupont" },
    { id: "CLI2", name: "Marie Curie" },
    { id: "CLI3", name: "Marie Curie" },
    { id: "CLI4", name: "Marie Curie" },
    { id: "CLI5", name: "Marie Curie" },
    
  ],
  clientsEnt: [
    { id: "ENT1", name: "Tesla Inc." },
    { id: "ENT2", name: "Amazon France" },
    { id: "ENT3", name: "Amazon France" },
    { id: "ENT4", name: "Amazon France" },
    { id: "ENT5", name: "Amazon France" },
    
  ],
  carts: [
    { id: "CART1", total: "89.99€" },
    { id: "CART2", total: "45.50€" },
    { id: "CART3", total: "45.50€" },
    { id: "CART4", total: "45.50€" },
    
  ],
  payments: [
    { id: "PAY1", status: "paid" },
    { id: "PAY2", status: "pending" },
    { id: "PAY3", status: "pending" },
    { id: "PAY4", status: "pending" },
    { id: "PAY5", status: "pending" },
    { id: "PAY6", status: "pending" },
    
  ],
  logs: [
    { id: "LOG1", message: "Utilisateur connecté", type: "info" },
    { id: "LOG2", message: "Erreur sur le serveur", type: "error" },
    { id: "LOG3", message: "Erreur sur le serveur", type: "error" },
    { id: "LOG4", message: "Erreur sur le serveur", type: "error" },
    { id: "LOG5", message: "Erreur sur le serveur", type: "error" },
    { id: "LOG6", message: "Erreur sur le serveur", type: "error" },
    
  ],
};

// 🎨 Fonction pour obtenir la couleur des badges
const getStatusColor = (status) => {
  switch (status) {
    case "pending": return "warning";
    case "confirmed": return "primary";
    case "shipped": return "secondary";
    case "delivered": return "success";
    case "canceled": return "danger";
    case "refunded": return "default";
    case "failed": return "error";
    default: return "gray";
  }
};

// 📌 Composant Principal
export default function DashboardBento() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      
      <motion.div variants={bentoVariants} initial="hidden" animate="visible" className="col-span-2">
        <Card shadow="sm" className="p-4">
          <OrderGraph/>
        </Card>
      </motion.div>

      {/*  Dernières Notifications */}
      <motion.div variants={bentoVariants} initial="hidden" animate="visible">
        <Card shadow="sm" className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">📌 Notifications</h3>
            <Bell size={20} className="text-gray-500" />
          </div>
          <ScrollShadow hideScrollBar className="mt-2 max-h-32">
            {fakeData.notifications.map((notif) => (
              <div key={notif.id} className="flex items-center py-2 border-b last:border-none">
                <Badge color={notif.read ? "default" : "primary"} className="mr-2" />
                <span className="text-sm">{notif.message}</span>
              </div>
            ))}
          </ScrollShadow>
        </Card>
      </motion.div>

      {/* Dernières Commandes */}
      <motion.div variants={bentoVariants} initial="hidden" animate="visible" className="col-span-2">
        <Card shadow="sm" className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Dernières Commandes</h3>
            <Package size={20} className="text-gray-500" />
          </div>
          {fakeData.orders.map((order) => (
            <div key={order.id} className="flex justify-between py-2 border-b last:border-none">
              <span className="text-sm">Commande {order.id}</span>
              <Badge color={getStatusColor(order.status)}>{order.status}</Badge>
            </div>
          ))}
        </Card>
      </motion.div>

      {/* 📌 Clients Individuels */}
      <motion.div variants={bentoVariants} initial="hidden" animate="visible">
        <Card shadow="sm" className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Clients Particuliers</h3>
            <Users size={20} className="text-gray-500" />
          </div>
          {fakeData.clientsInd.map((client) => (
            <div key={client.id} className="py-2 border-b last:border-none">
              <span className="text-sm">{client.name}</span>
            </div>
          ))}
        </Card>
      </motion.div>

      {/* 📌 Clients Entreprises */}
      <motion.div variants={bentoVariants} initial="hidden" animate="visible">
        <Card shadow="sm" className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Clients Entreprises</h3>
            <Building size={20} className="text-gray-500" />
          </div>
          {fakeData.clientsEnt.map((client) => (
            <div key={client.id} className="py-2 border-b last:border-none">
              <span className="text-sm">{client.name}</span>
            </div>
          ))}
        </Card>
      </motion.div>

      {/* 📌 Derniers Paniers */}
      <motion.div variants={bentoVariants} initial="hidden" animate="visible">
        <Card shadow="sm" className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Derniers Paniers</h3>
            <ShoppingCart size={20} className="text-gray-500" />
          </div>
          {fakeData.carts.map((cart) => (
            <div key={cart.id} className="py-2 border-b last:border-none">
              <span className="text-sm">Panier {cart.id} - {cart.total}</span>
            </div>
          ))}
        </Card>
      </motion.div>

      {/* 📌 Derniers Logs */}
      <motion.div variants={bentoVariants} initial="hidden" animate="visible">
        <Card shadow="sm" className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Logs Système</h3>
            <FileText size={20} className="text-gray-500" />
          </div>
          {fakeData.logs.map((log) => (
            <div key={log.id} className="py-2 border-b last:border-none">
              <AlertTriangle size={16} className="mr-2 text-red-500" />
              <span className="text-sm">{log.message}</span>
            </div>
          ))}
        </Card>
      </motion.div>

    </div>
  );
}
